import { type MenuInputOption } from '@saviynt/design-system';

import { type InvertedLocalize } from '../../../shared/utils/localization';

/**
 * Maps short NameID format keys to their full SAML URN values.
 * Used to match metadata file values to dropdown options.
 */
const NAME_ID_FORMAT_URNS: Record<string, string> = {
  emailAddress: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
  transient: 'urn:oasis:names:tc:SAML:2.0:nameid-format:transient',
  persistent: 'urn:oasis:names:tc:SAML:2.0:nameid-format:persistent',
  X509SubjectName: 'urn:oasis:names:tc:SAML:1.1:nameid-format:X509SubjectName',
  unspecified: 'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified',
};

const DEFAULT_NAME_ID_FORMAT = 'emailAddress';

/** Build MenuInput options for the Name ID Format dropdown */
export function getNameIdFormatOptions(l: InvertedLocalize): MenuInputOption[] {
  return [
    { label: l('EmailAddress'), value: 'emailAddress' },
    { label: l('Transient'), value: 'transient' },
    { label: l('Persistent'), value: 'persistent' },
    { label: l('x509SubjectName'), value: 'X509SubjectName' },
    { label: l('Unspecified'), value: 'unspecified' },
  ];
}

/**
 * Attempts to resolve a full SAML URN (or short key) to a short key.
 * Matching is case-insensitive so that API responses like "Persistent"
 * or "EMAILADDRESS" still map to the correct dropdown option.
 * Returns DEFAULT_NAME_ID_FORMAT if the input is empty or not recognized.
 */
export function resolveNameIdFormat(urnOrKey: string): string {
  if (!urnOrKey) {
    return DEFAULT_NAME_ID_FORMAT;
  }

  // Exact short key match
  if (urnOrKey in NAME_ID_FORMAT_URNS) {
    return urnOrKey;
  }

  // Case-insensitive short key match
  const lowerInput = urnOrKey.toLowerCase();
  const caseInsensitiveKey = Object.keys(NAME_ID_FORMAT_URNS).find(
    (key) => key.toLowerCase() === lowerInput
  );

  if (caseInsensitiveKey) {
    return caseInsensitiveKey;
  }

  // Reverse lookup from URN (exact match)
  const entry = Object.entries(NAME_ID_FORMAT_URNS).find(
    ([, urn]) => urn === urnOrKey
  );

  if (entry) {
    return entry[0];
  }

  // Case-insensitive URN suffix match (e.g. extract "persistent" from the URN tail)
  const urnEntry = Object.entries(NAME_ID_FORMAT_URNS).find(
    ([, urn]) => urn.toLowerCase() === lowerInput
  );

  return urnEntry?.[0] ?? DEFAULT_NAME_ID_FORMAT;
}

/** Converts a short key to its full SAML URN. Returns the key as-is if not found. */
export function toNameIdUrn(shortKey: string): string {
  if (shortKey in NAME_ID_FORMAT_URNS) {
    return NAME_ID_FORMAT_URNS[shortKey];
  }

  return shortKey;
}

export enum PrincipalType {
  ATTRIBUTE = 'ATTRIBUTE',
  FRIENDLY_ATTRIBUTE = 'FRIENDLY_ATTRIBUTE',
}

/** Build MenuInput options for the Principal Type dropdown */
export function getPrincipalTypeOptions(
  l: InvertedLocalize
): MenuInputOption[] {
  return [
    { label: l('Attribute'), value: PrincipalType.ATTRIBUTE },
    {
      label: l('Friendly Attribute'),
      value: PrincipalType.FRIENDLY_ATTRIBUTE,
    },
  ];
}

export { DEFAULT_NAME_ID_FORMAT, NAME_ID_FORMAT_URNS };
